import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';
import { Content } from '../types';
import { getFileTypeInfo, formatFileSize, truncateFileName } from '../utils/fileUtils';
import CategoryAutocomplete from '../components/UI/CategoryAutocomplete';
import TagAutocomplete from '../components/Tag/TagAutocomplete';

const ContentEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [content, setContent] = useState<Content | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [timestamp, setTimestamp] = useState('');
  const [lnks, setLnks] = useState('');
  const [files, setFiles] = useState<any[]>([]);
  const [newFile, setNewFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [category1Id, setCategory1Id] = useState<number | ''>('');
  const [category2Id, setCategory2Id] = useState<number | ''>('');
  const [category3Id, setCategory3Id] = useState<number | ''>('');
  const [tags, setTags] = useState<string[]>([]);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await axiosInstance.get(`/api/contents/${id}`);
        const contentData = res.data;
        setContent(contentData);
        setTitle(contentData.title || '');
        setDescription(contentData.description || '');
        setTimestamp(contentData.timestamp ? new Date(contentData.timestamp).toISOString().slice(0, 16) : '');
        setLnks(contentData.lnks || '');
        setCategory1Id(contentData.main_category1_id || '');
        setCategory2Id(contentData.main_category2_id || '');
        setCategory3Id(contentData.main_category3_id || '');
        
        // 태그 로드
        const tagsRes = await axiosInstance.get(`/api/tags/content/${id}`);
        setTags(tagsRes.data.map((tag: any) => tag.name));
        
        // 첨부파일 목록 불러오기
        const filesRes = await axiosInstance.get(`/api/attach/${id}`);
        setFiles(filesRes.data);
      } catch (err) {
        console.error('컨텐츠 로딩 실패:', err);
        alert('컨텐츠를 불러올 수 없습니다.');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchContent();
    }
  }, [id, navigate]);

  // 첨부파일 삭제
  const handleDeleteFile = async (fileId: number) => {
    if (!window.confirm('첨부파일을 삭제하시겠습니까?')) return;
    
    try {
      await axiosInstance.delete(`/api/attach/${fileId}`);
      setFiles(files.filter(file => file.id !== fileId));
      alert('첨부파일이 삭제되었습니다.');
    } catch (err: any) {
      alert('첨부파일 삭제에 실패했습니다.');
    }
  };

  // 새 첨부파일 추가
  const handleAddFile = async () => {
    if (!newFile) return;
    
    try {
      const formData = new FormData();
      formData.append('file', newFile);
      formData.append('content_id', id!);
      formData.append('description', '');
      
      await axiosInstance.post('/api/attach', formData, { 
        headers: { 'Content-Type': 'multipart/form-data' } 
      });
      
      // 새로운 파일 목록 가져오기
      const filesRes = await axiosInstance.get(`/api/attach/${id}`);
      setFiles(filesRes.data);
      setNewFile(null);
      
      // 파일 input 초기화
      const fileInput = document.getElementById('newFileInput') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      
      alert('첨부파일이 추가되었습니다.');
    } catch (err: any) {
      alert('첨부파일 추가에 실패했습니다.');
    }
  };

  const selectedCategoryIds = [category1Id, category2Id, category3Id].filter(
    (categoryId): categoryId is number => typeof categoryId === 'number'
  );

  const handleCategoryChange = (ids: number[]) => {
    setCategory1Id(ids[0] || '');
    setCategory2Id(ids[1] || '');
    setCategory3Id(ids[2] || '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) {
      alert('제목과 설명을 입력해주세요.');
      return;
    }

    setSubmitting(true);
    try {
      await axiosInstance.put(`/api/contents/${id}`, {
        title: title.trim(),
        description: description.trim(),
        timestamp,
        lnks: lnks.trim() || null,
        main_category1_id: category1Id || null,
        main_category2_id: category2Id || null,
        main_category3_id: category3Id || null,
        topic_area_id: content?.topic_area_id || null,
        tags
      });

      alert('컨텐츠가 수정되었습니다.');
      navigate(`/content/${id}`);
    } catch (err: any) {
      if (err.response?.status === 401) {
        alert('로그인이 필요합니다.');
        navigate('/login');
      } else if (err.response?.status === 403) {
        alert('수정 권한이 없습니다.');
        navigate(`/content/${id}`);
      } else {
        alert('컨텐츠 수정에 실패했습니다.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('수정을 취소하시겠습니까?')) {
      navigate(`/content/${id}`);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!content) {
    return <div>컨텐츠를 찾을 수 없습니다.</div>;
  }

  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: 24 }}>
      <h2>컨텐츠 수정</h2>
      
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
            제목 *
          </label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            style={{
              width: '100%',
              padding: 12,
              border: '1px solid #ddd',
              borderRadius: 4,
              fontSize: 16
            }}
            required
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
            설명 *
          </label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            style={{
              width: '100%',
              minHeight: 120,
              padding: 12,
              border: '1px solid #ddd',
              borderRadius: 4,
              fontSize: 14,
              resize: 'vertical'
            }}
            required
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
            날짜 및 시간 *
          </label>
          <input
            type="datetime-local"
            value={timestamp}
            onChange={e => setTimestamp(e.target.value)}
            style={{
              width: '100%',
              padding: 12,
              border: '1px solid #ddd',
              borderRadius: 4,
              fontSize: 14
            }}
            required
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
            관련링크 (여러 개인 경우 ;로 구분)
          </label>
          <input
            type="text"
            value={lnks}
            onChange={e => setLnks(e.target.value)}
            placeholder="https://example.com;https://example2.com"
            style={{
              width: '100%',
              padding: 12,
              border: '1px solid #ddd',
              borderRadius: 4,
              fontSize: 14
            }}
          />
        </div>

        {/* 카테고리 선택 */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
            카테고리 (각각 독립적으로 선택 가능)
          </label>
          <CategoryAutocomplete
            selectedIds={selectedCategoryIds}
            onChange={handleCategoryChange}
            maxSelections={3}
          />
        </div>

        {/* 태그 입력 */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
            태그
          </label>
          <TagAutocomplete selectedTags={tags} onChange={setTags} />
        </div>

        {/* 기존 첨부파일 섹션 */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
            기존 첨부파일
          </label>
          {files.length === 0 ? (
            <div style={{ padding: 16, backgroundColor: '#f8f9fa', borderRadius: 4, color: '#6c757d' }}>
              첨부파일이 없습니다.
            </div>
          ) : (
            <div style={{ border: '1px solid #ddd', borderRadius: 4, padding: 12 }}>
              {files.map(file => {
                // file_type이 이미 라벨형태인지 MIME 타입인지 확인
                const isLabel = !file.file_type?.includes('/') && !file.file_type?.includes('application');
                const fileInfo = isLabel 
                  ? (() => {
                      // 라벨에서 적절한 아이콘 선택
                      const labelIconMap: Record<string, string> = {
                        'Word 문서': '📄',
                        'Excel 문서': '📊', 
                        'PowerPoint 문서': '📽️',
                        'PDF 문서': '📋',
                        '이미지': '🖼️',
                        '비디오': '🎬',
                        '오디오': '🎵',
                        '압축 파일': '📦',
                        '텍스트': '📝'
                      };
                      const icon = labelIconMap[file.file_type] || '📎';
                      const colorMap: Record<string, string> = {
                        'Word 문서': '#2b579a',
                        'Excel 문서': '#217346',
                        'PowerPoint 문서': '#d24726',
                        'PDF 문서': '#dc3545',
                        '이미지': '#6f42c1',
                        '비디오': '#fd7e14',
                        '오디오': '#20c997',
                        '압축 파일': '#6c757d',
                        '텍스트': '#495057'
                      };
                      const color = colorMap[file.file_type] || '#007bff';
                      return { icon, label: file.file_type, color };
                    })()
                  : getFileTypeInfo(file.file_name, file.file_type);

                return (
                  <div key={file.id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRadius: 4,
                    marginBottom: 8
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      <span style={{ fontSize: '20px', marginRight: 8 }}>
                        {fileInfo.icon}
                      </span>
                      <div>
                        <div style={{ fontWeight: '500', color: fileInfo.color }}>
                          {truncateFileName(file.file_name, 30)}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6c757d' }}>
                          {fileInfo.label} {file.file_size && `• ${formatFileSize(file.file_size)}`}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeleteFile(file.id)}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      삭제
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 새 첨부파일 추가 섹션 */}
        <div style={{ marginBottom: 30 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
            새 첨부파일 추가
          </label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              id="newFileInput"
              type="file"
              onChange={e => setNewFile(e.target.files?.[0] || null)}
              style={{ flex: 1 }}
            />
            <button
              type="button"
              onClick={handleAddFile}
              disabled={!newFile}
              style={{
                padding: '8px 16px',
                backgroundColor: newFile ? '#28a745' : '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: newFile ? 'pointer' : 'not-allowed',
                fontSize: '14px'
              }}
            >
              추가
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={handleCancel}
            style={{
              padding: '12px 24px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer'
            }}
          >
            취소
          </button>
          <button
            type="submit"
            disabled={submitting}
            style={{
              padding: '12px 24px',
              backgroundColor: submitting ? '#6c757d' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: submitting ? 'not-allowed' : 'pointer'
            }}
          >
            {submitting ? '수정 중...' : '수정하기'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContentEditPage; 
